/**
 * Petites fenêtres de dialogue communes à toute l'application, utilisables
 * comme des promesses :
 *
 *   const r = await demanderMotif({ titre: 'Annuler le paiement', message: '…' })
 *   if (!r) return            // l'utilisateur a renoncé
 *   api.post(..., { motif: r.motif })
 *
 * Remplace prompt() / confirm() : motif obligatoire, texte explicatif,
 * choix facultatif (moyen de remboursement…), accessible au clavier.
 */
function el(tag, attrs = {}, enfants = []) {
  const n = document.createElement(tag)
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === 'text') n.textContent = v
    else if (k === 'html') n.innerHTML = v
    else if (k.startsWith('on')) n.addEventListener(k.slice(2), v)
    else n.setAttribute(k, v)
  })
  enfants.forEach(c => c && n.appendChild(c))
  return n
}

function fenetre({ titre, message, danger = true, contenu, valider, libelleValider = 'Confirmer', libelleAnnuler = 'Retour' }) {
  return new Promise(resolve => {
    const precedent = document.activeElement
    const fond = el('div', { class: 'dlg-fond', role: 'dialog', 'aria-modal': 'true' })
    const erreur = el('div', { class: 'alert alert-danger py-2 small mt-2 d-none' })
    const fermer = (valeur) => {
      document.removeEventListener('keydown', clavier)
      fond.remove()
      precedent?.focus?.()
      resolve(valeur)
    }
    const confirmer = () => {
      const r = valider ? valider() : true
      if (r && r.erreur) {
        erreur.textContent = r.erreur
        erreur.classList.remove('d-none')
        return
      }
      fermer(r)
    }
    const clavier = (ev) => {
      if (ev.key === 'Escape') fermer(null)
      if (ev.key === 'Enter' && (ev.ctrlKey || ev.target.tagName !== 'TEXTAREA')) { ev.preventDefault(); confirmer() }
    }
    const carte = el('div', { class: 'dlg-carte card shadow' }, [
      el('div', { class: 'card-header d-flex justify-content-between align-items-center' }, [
        el('h5', { class: 'mb-0', text: titre }),
        el('button', { type: 'button', class: 'btn-close', 'aria-label': 'Fermer', onclick: () => fermer(null) }),
      ]),
      el('div', { class: 'card-body' }, [
        message ? el('p', { class: 'mb-3', text: message }) : null, // texte brut : les noms saisis ne sont jamais interprétés
        contenu || null,
        erreur,
      ]),
      el('div', { class: 'card-footer text-end' }, [
        el('button', { type: 'button', class: 'btn btn-outline-secondary me-2', text: libelleAnnuler, onclick: () => fermer(null) }),
        el('button', { type: 'button', class: 'btn ' + (danger ? 'btn-danger' : 'btn-primary'), text: libelleValider, onclick: confirmer }),
      ]),
    ])
    fond.appendChild(carte)
    fond.addEventListener('mousedown', (ev) => { if (ev.target === fond) fermer(null) })
    if (!document.getElementById('dlg-style')) {
      document.head.appendChild(el('style', { id: 'dlg-style', text: `
        .dlg-fond{position:fixed;inset:0;background:rgba(15,23,42,.45);z-index:2100;display:flex;align-items:flex-start;justify-content:center;padding:8vh 16px;overflow-y:auto}
        .dlg-carte{width:100%;max-width:520px;border-radius:12px;border:none}
        .dlg-carte .card-header{background:#fff;border-bottom:1px solid #eef1f5}
        .dlg-carte .card-footer{background:#fafbfc;border-top:1px solid #eef1f5}` }))
    }
    document.body.appendChild(fond)
    document.addEventListener('keydown', clavier)
    setTimeout(() => (carte.querySelector('textarea,select,input') || carte.querySelector('.btn-danger,.btn-primary'))?.focus(), 30)
  })
}

/**
 * Demande un motif (obligatoire, 3 caractères minimum).
 * Option « choix » : { libelle, options: [{ valeur, texte }], valeur } (ex. moyen de remboursement).
 * @returns {Promise<{motif: string, choix?: any}|null>}
 */
export function demanderMotif({ titre = 'Annuler', message = '', libelle = 'Motif', placeholder = 'Pourquoi ?', libelleValider = "Confirmer l'annulation", choix = null, danger = true } = {}) {
  const zone = el('textarea', { class: 'form-control', rows: '3', placeholder, maxlength: '255' })
  let select = null
  const contenu = el('div', {}, [
    el('label', { class: 'form-label small fw-semibold', text: libelle }),
    zone,
  ])
  if (choix) {
    select = el('select', { class: 'form-select' }, (choix.options || []).map(o => {
      const opt = el('option', { value: String(o.valeur), text: o.texte })
      if (String(o.valeur) === String(choix.valeur ?? '')) opt.selected = true
      return opt
    }))
    contenu.appendChild(el('label', { class: 'form-label small fw-semibold mt-3', text: choix.libelle }))
    contenu.appendChild(select)
  }
  return fenetre({
    titre, message, danger, contenu, libelleValider,
    valider: () => {
      const motif = zone.value.trim()
      if (motif.length < 3) return { erreur: 'Le motif est obligatoire.' }
      return { motif, choix: select ? select.value : undefined }
    },
  })
}

/**
 * Confirmation simple (remplace confirm()). « contenu » : élément facultatif
 * affiché sous le message (ex. champ de commentaire lu après validation).
 * @returns {Promise<boolean>}
 */
export async function confirmer({ titre = 'Confirmer', message = '', libelleValider = 'Confirmer', danger = false, contenu = null } = {}) {
  return !!(await fenetre({ titre, message, danger, libelleValider, contenu }))
}

export default { demanderMotif, confirmer }
